/**
 * Agendas bloqueadas (2026-08-26): festivos, vacaciones,
 * congresos… Se eligen días en el calendario (sueltos o seguidos), las
 * agendas afectadas (una, varias o todas) y el motivo; el servidor crea los
 * bloqueos de día completo y desde ese momento es imposible citar ahí (lo
 * impone el backend, no solo la vista). Abajo, los bloqueos vigentes con su
 * botón de desbloquear.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CalendarOff, ChevronLeft, ChevronRight, Loader2, Lock, Trash2 } from 'lucide-react';
import { api, formatDate } from '../../lib/api';
import { listBloqueos, deleteBloqueo } from '../../lib/citasApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

// Sugerencias genéricas; el motivo se puede escribir a mano.
const MOTIVOS = ['Festivo', 'Vacaciones del médico', 'Congreso', 'Formación'];
const DIAS_SEMANA = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export default function BloqueosAgendaDialog({ open, onOpenChange, agendas = [], onChanged }) {
  const hoy = new Date();
  const [mes, setMes] = useState(() => new Date(hoy.getFullYear(), hoy.getMonth(), 1));
  const [seleccion, setSeleccion] = useState(() => new Set()); // 'YYYY-MM-DD'
  const [agendasSel, setAgendasSel] = useState(() => new Set());
  const [motivo, setMotivo] = useState('Festivo');
  const [motivoLibre, setMotivoLibre] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [existentes, setExistentes] = useState([]); // bloqueos vigentes (todas las agendas)
  const [cargandoExistentes, setCargandoExistentes] = useState(false);

  const nombreAgenda = useMemo(() => {
    const m = {};
    agendas.forEach((a) => { m[a.id] = a.name; });
    return m;
  }, [agendas]);

  // Bloqueos vigentes o futuros de TODAS las agendas, para verlos y quitarlos.
  const cargarExistentes = useCallback(async () => {
    if (!agendas.length) return;
    setCargandoExistentes(true);
    try {
      const desde = ymd(new Date());
      const listas = await Promise.all(
        agendas.map((a) => listBloqueos(a.id, { dateFrom: desde }).catch(() => [])),
      );
      const todos = listas.flat().filter((b) => b.all_day !== false);
      todos.sort((x, y) => (x.date_from || '').localeCompare(y.date_from || ''));
      setExistentes(todos);
    } finally {
      setCargandoExistentes(false);
    }
  }, [agendas]);

  useEffect(() => {
    if (!open) return;
    setSeleccion(new Set());
    setAgendasSel(new Set());
    setMotivo('Festivo');
    setMotivoLibre('');
    setMes(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
    cargarExistentes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cargarExistentes]);

  // Días de este mes que YA tienen algún bloqueo (para pintarlos en el calendario).
  const bloqueadosDelMes = useMemo(() => {
    const set = new Set();
    const y = mes.getFullYear(); const m = mes.getMonth();
    const ultimo = new Date(y, m + 1, 0).getDate();
    for (let d = 1; d <= ultimo; d += 1) {
      const f = ymd(new Date(y, m, d));
      if (existentes.some((b) => b.date_from <= f && f <= b.date_to)) set.add(f);
    }
    return set;
  }, [existentes, mes]);

  const alternarDia = (f) => setSeleccion((prev) => {
    const next = new Set(prev);
    if (next.has(f)) next.delete(f); else next.add(f);
    return next;
  });
  const alternarAgenda = (id) => setAgendasSel((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const semanas = useMemo(() => {
    const y = mes.getFullYear(); const m = mes.getMonth();
    const primero = new Date(y, m, 1);
    // Lunes = 0
    const offset = (primero.getDay() + 6) % 7;
    const ultimo = new Date(y, m + 1, 0).getDate();
    const celdas = [];
    for (let i = 0; i < offset; i += 1) celdas.push(null);
    for (let d = 1; d <= ultimo; d += 1) celdas.push(new Date(y, m, d));
    while (celdas.length % 7 !== 0) celdas.push(null);
    const out = [];
    for (let i = 0; i < celdas.length; i += 7) out.push(celdas.slice(i, i + 7));
    return out;
  }, [mes]);

  const motivoFinal = motivo === '__otro__' ? motivoLibre.trim() : motivo;

  const bloquear = async () => {
    if (!seleccion.size) return toast.error('Elige al menos un día en el calendario');
    if (!agendasSel.size) return toast.error('Elige a qué agendas aplica el bloqueo');
    if (!motivoFinal) return toast.error('Indica el motivo del bloqueo');
    setGuardando(true);
    try {
      const r = await api.post('/api/agendas/bloqueos-lote', {
        agenda_ids: Array.from(agendasSel),
        fechas: Array.from(seleccion),
        motivo: motivoFinal,
      });
      toast.success(`${seleccion.size} día${seleccion.size === 1 ? '' : 's'} bloqueado${seleccion.size === 1 ? '' : 's'} en ${agendasSel.size} agenda${agendasSel.size === 1 ? '' : 's'} — ${motivoFinal}`);
      setSeleccion(new Set());
      await cargarExistentes();
      onChanged?.();
      return r;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudieron crear los bloqueos');
    } finally {
      setGuardando(false);
    }
  };

  const desbloquear = async (b) => {
    if (!window.confirm(`¿Desbloquear ${nombreAgenda[b.agenda_id] || 'la agenda'} (${formatDate(b.date_from)}${b.date_to !== b.date_from ? ` – ${formatDate(b.date_to)}` : ''})?`)) return;
    try {
      await deleteBloqueo(b.id);
      toast.success('Desbloqueado');
      await cargarExistentes();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo desbloquear');
    }
  };

  const hoyStr = ymd(hoy);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bloqueos-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarOff className="w-5 h-5 text-[#1d4ed8]" /> Agendas bloqueadas
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Calendario: clic para marcar/desmarcar días */}
          <div className="rounded-lg border border-border p-3">
            <div className="flex items-center justify-between mb-2">
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                onClick={() => setMes((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
                data-testid="bloqueos-mes-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="text-sm font-semibold text-[#13231E] capitalize">
                {MESES[mes.getMonth()]} {mes.getFullYear()}
              </div>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                onClick={() => setMes((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
                data-testid="bloqueos-mes-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-muted-foreground mb-1">
              {DIAS_SEMANA.map((d) => <div key={d}>{d}</div>)}
            </div>
            {semanas.map((sem, i) => (
              <div key={i} className="grid grid-cols-7 gap-1 mb-1">
                {sem.map((d, j) => {
                  if (!d) return <div key={j} />;
                  const f = ymd(d);
                  const sel = seleccion.has(f);
                  const yaBloqueado = bloqueadosDelMes.has(f);
                  const pasado = f < hoyStr;
                  return (
                    <button
                      key={j}
                      type="button"
                      disabled={pasado}
                      onClick={() => alternarDia(f)}
                      data-testid={`bloqueos-dia-${f}`}
                      title={yaBloqueado ? 'Este día ya tiene algún bloqueo' : ''}
                      className={`h-9 rounded-md text-sm tabular-nums transition-colors relative
                        ${pasado ? 'text-muted-foreground/40 cursor-not-allowed'
                          : sel ? 'bg-[#1d4ed8] text-white font-semibold'
                          : 'hover:bg-[#1d4ed8]/10'}
                        ${f === hoyStr && !sel ? 'ring-1 ring-[#2E6E5E]/50' : ''}`}
                    >
                      {d.getDate()}
                      {yaBloqueado && !sel && (
                        <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#1d4ed8]" />
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground mt-1">
              Marca días sueltos o seguidos (los seguidos se agrupan solos). El punto azul: día con algún bloqueo ya puesto.
            </p>
          </div>

          {/* Agendas afectadas */}
          <div>
            <Label>¿A qué agendas aplica?</Label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => setAgendasSel((prev) => (prev.size === agendas.length ? new Set() : new Set(agendas.map((a) => a.id))))}
                data-testid="bloqueos-todas"
                className={`px-2.5 py-1 rounded-full border text-xs font-medium ${
                  agendasSel.size === agendas.length && agendas.length > 0
                    ? 'border-[#1d4ed8] bg-[#1d4ed8]/10 text-[#1d4ed8]' : 'border-border'}`}
              >
                Todas
              </button>
              {agendas.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => alternarAgenda(a.id)}
                  data-testid={`bloqueos-agenda-${a.id}`}
                  className={`px-2.5 py-1 rounded-full border text-xs font-medium ${
                    agendasSel.has(a.id) ? 'border-[#1d4ed8] bg-[#1d4ed8]/10 text-[#1d4ed8]' : 'border-border'}`}
                >
                  {a.name}
                </button>
              ))}
            </div>
          </div>

          {/* Motivo */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label>Motivo</Label>
              <Select value={motivo} onValueChange={setMotivo}>
                <SelectTrigger data-testid="bloqueos-motivo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MOTIVOS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  <SelectItem value="__otro__">Otro motivo…</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {motivo === '__otro__' && (
              <div>
                <Label>Escribe el motivo</Label>
                <Input value={motivoLibre} onChange={(e) => setMotivoLibre(e.target.value)}
                  placeholder="Obras, formación…" data-testid="bloqueos-motivo-libre" />
              </div>
            )}
          </div>

          <Button
            onClick={bloquear}
            disabled={guardando || !seleccion.size || !agendasSel.size || !motivoFinal}
            className="w-full bg-[#1d4ed8] hover:bg-[#1e40af]"
            data-testid="bloqueos-guardar"
          >
            {guardando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
            Bloquear {seleccion.size || ''} día{seleccion.size === 1 ? '' : 's'}
            {agendasSel.size ? ` en ${agendasSel.size} agenda${agendasSel.size === 1 ? '' : 's'}` : ''}
          </Button>

          {/* Bloqueos vigentes */}
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
              Bloqueos vigentes y futuros
            </div>
            {cargandoExistentes ? (
              <div className="py-3 text-center text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Cargando…
              </div>
            ) : existentes.length === 0 ? (
              <p className="text-sm text-muted-foreground">No hay días bloqueados a partir de hoy.</p>
            ) : (
              <div className="divide-y divide-border rounded-lg border border-border">
                {existentes.map((b) => (
                  <div key={b.id} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`bloqueo-fila-${b.id}`}>
                    <Lock className="w-3.5 h-3.5 text-[#1d4ed8] shrink-0" />
                    <div className="min-w-0 flex-1">
                      <span className="font-medium">{nombreAgenda[b.agenda_id] || 'Agenda'}</span>
                      <span className="text-muted-foreground"> · {formatDate(b.date_from)}{b.date_to !== b.date_from ? ` – ${formatDate(b.date_to)}` : ''}</span>
                      {b.reason ? <span className="text-muted-foreground"> · {b.reason}</span> : null}
                    </div>
                    <button
                      type="button"
                      onClick={() => desbloquear(b)}
                      className="shrink-0 text-muted-foreground hover:text-destructive"
                      title="Desbloquear"
                      data-testid={`bloqueo-quitar-${b.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
